<template>
  <nav class="absolute top-4 left-0 z-60 flex w-full shrink-0">
    <section class="mx-[16px] flex flex-1 items-start justify-between gap-4 pb-1">
      <section
        class="relative flex w-full items-center justify-between gap-2 rounded-lg bg-white p-2 shadow-md"
      >
        <!-- Logo -->
        <div class="flex items-center gap-2">
          <router-link :to="landingUrl" data-test="logo-home-link">
            <Logo v-if="!logo" class="h-9 w-9" />
            <img v-else :src="logo" class="h-9 max-w-14 object-contain" />
          </router-link>
          <HomeToolbar v-if="isLoggedIn" />
        </div>

        <h1
          v-if="route.name === 'Landing'"
          class="text-agentnoon-primary-700 hidden text-xl font-medium min-[885px]:block lg:text-2xl"
        >
          👋 Welcome
        </h1>

        <h1
          v-else-if="route.name === 'Settings'"
          class="text-agentnoon-primary-700 hidden text-xl font-medium min-[885px]:block lg:text-2xl"
        >
          Settings
        </h1>

        <h1
          v-else-if="route.name === 'InviteAccessUsers'"
          class="text-agentnoon-primary-700 hidden text-xl font-medium min-[885px]:block lg:text-2xl"
        >
          Access Control
        </h1>

        <h1
          v-else-if="route.name === 'UserGroups'"
          class="text-agentnoon-primary-700 hidden text-xl font-medium min-[885px]:block lg:text-2xl"
        >
          User Groups
        </h1>

        <h1
          v-else-if="route.name === 'Super Admin'"
          class="text-agentnoon-primary-700 hidden text-xl font-medium min-[885px]:block lg:text-2xl"
        >
          Super Admin
        </h1>

        <h1
          v-else-if="route.name === 'Notifications'"
          class="text-agentnoon-primary-700 hidden text-xl font-medium min-[885px]:block lg:text-2xl"
        >
          Notifications
        </h1>

        <div class="flex justify-end gap-2">
          <div
            class="group hover:text-agentnoon-primary-700 hover:rounded-lg hover:bg-gray-50"
            :class="{
              'text-agentnoon-primary-700 rounded-lg bg-gray-50': $route.path === landingUrl
            }"
          >
            <PopperTooltip content="Home" hover open-delay="300">
              <router-link
                :to="landingUrl"
                data-test="home-button"
                class="group-hover:text-agentnoon-primary-700 flex w-full cursor-pointer items-center px-4 py-2.5 text-left text-sm font-normal whitespace-nowrap text-gray-500"
                :class="{ 'text-agentnoon-primary-700': $route.path === landingUrl }"
              >
                <IconHome
                  class="hover:text-agentnoon-primary-700 h-4 w-4 transition-colors duration-200"
                  :class="{
                    'group-hover:text-agentnoon-primary-700 text-gray-500':
                      $route.path !== landingUrl,
                    'text-agentnoon-primary-700': $route.path === landingUrl
                  }"
                />
              </router-link>
            </PopperTooltip>
          </div>

          <NotificationsMenu variant="nav" :active="route.name === 'Notifications'" />

          <div
            v-if="!shouldShow && isAdmin"
            class="group hover:text-agentnoon-primary-700 hover:rounded-lg hover:bg-gray-50"
            :class="{
              'text-agentnoon-primary-700 rounded-lg bg-gray-50': route.path.includes('/settings')
            }"
          >
            <PopperTooltip content="Settings" hover open-delay="300">
              <router-link
                :to="navigationConfig[FEATURES.Settings].url"
                data-test="settings-button"
                class="flex w-full cursor-pointer items-center py-2.5 pr-4 pl-4 text-left text-sm font-normal whitespace-nowrap text-gray-500"
                :class="{ 'text-agentnoon-primary-700': route.path.includes('/settings') }"
              >
                <component
                  :is="CogIcon"
                  class="hover:text-agentnoon-primary-700 h-4 w-4 transition-colors duration-200"
                  :class="{
                    'group-hover:text-agentnoon-primary-700 text-gray-500':
                      !route.path.includes('/settings'),
                    'text-agentnoon-primary-700': route.path.includes('/settings')
                  }"
                />
              </router-link>
            </PopperTooltip>
          </div>

          <div
            v-if="!shouldShow && isAdmin"
            class="group hover:text-agentnoon-primary-700 hover:rounded-lg hover:bg-gray-50"
            :class="{
              'text-agentnoon-primary-700 rounded-lg bg-gray-50':
                route.path.includes('/invite-access')
            }"
          >
            <PopperTooltip content="Access control" hover open-delay="300">
              <router-link
                :to="navigationConfig[FEATURES.AccessControl].url"
                data-test="access-control-button"
                class="flex w-full cursor-pointer items-center px-4 py-2.5 text-left text-sm font-normal whitespace-nowrap text-gray-500"
                :class="{
                  'text-agentnoon-primary-700':
                    $route.path === route.path.includes('/invite-access')
                }"
              >
                <component
                  :is="UserIcon"
                  class="group-hover:text-agentnoon-primary-700 h-4 w-4 transition-colors duration-200"
                  :class="{
                    'group-hover:text-agentnoon-primary-700 text-gray-500':
                      !route.path.includes('/invite-access'),
                    'text-agentnoon-primary-700': $route.path.includes('/invite-access')
                  }"
                />
              </router-link>
            </PopperTooltip>
          </div>

          <div
            v-if="!shouldShow && isAdmin"
            class="group hover:text-agentnoon-primary-700 hover:rounded-lg hover:bg-gray-50"
            :class="{
              'text-agentnoon-primary-700 rounded-lg bg-gray-50':
                route.path.includes('/user-groups')
            }"
          >
            <PopperTooltip content="User groups" hover open-delay="300">
              <router-link
                :to="navigationConfig[FEATURES.UserGroups].url"
                data-test="user-groups-button"
                class="flex w-full cursor-pointer items-center px-4 py-2.5 text-left text-sm font-normal whitespace-nowrap text-gray-500"
                :class="{
                  'text-agentnoon-primary-700': route.path.includes('/user-groups')
                }"
              >
                <component
                  :is="UsersGroupIcon"
                  class="group-hover:text-agentnoon-primary-700 h-4 w-4 transition-colors duration-200"
                  :class="{
                    'group-hover:text-agentnoon-primary-700 text-gray-500':
                      !route.path.includes('/user-groups'),
                    'text-agentnoon-primary-700': $route.path.includes('/user-groups')
                  }"
                />
              </router-link>
            </PopperTooltip>
          </div>

          <div class="group hover:text-agentnoon-primary-700 hover:rounded-lg hover:bg-gray-50">
            <PopperTooltip content="Help center" hover open-delay="300">
              <a
                href="https://help.agentnoon.com"
                target="blank"
                data-test="help-center-button"
                class="hover:text-agentnoon-primary-700 flex w-full cursor-pointer items-center py-2.5 pr-4 pl-4 text-left text-sm font-normal whitespace-nowrap text-gray-500 transition-colors hover:rounded-lg hover:bg-gray-50"
              >
                <component
                  :is="IconHelp"
                  class="group-hover:text-agentnoon-primary-700 h-4 w-4 transition-colors duration-200"
                />
              </a>
            </PopperTooltip>
          </div>

          <Menu
            v-if="user"
            as="div"
            class="relative mr-2 flex shrink-0 items-center justify-center"
          >
            <MenuButton
              data-test="user-menu-button"
              class="hover:ring-agentnoon-primary mr-1 transform rounded-full transition-all hover:scale-110 hover:ring-2"
            >
              <img v-if="userPic" :src="userPic" class="h-6 w-6 rounded-full" :alt="userName" />
              <div v-else class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-50">
                <PersonIcon class="h-4 w-4" aria-hidden="true" />
              </div>
            </MenuButton>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <HeadlessMenuItems
                class="absolute top-12 right-0 min-w-[200px] origin-top-right rounded-md bg-white p-2 shadow-lg ring-1 ring-black/5 focus:outline-hidden"
              >
                <section class="flex w-full flex-col justify-center gap-1 text-xs text-white">
                  <section
                    class="flex w-full flex-col justify-center gap-1 rounded-md bg-gray-50 p-2"
                  >
                    <div class="text-bluedark-700 flex justify-between gap-2">
                      <span v-if="userName" class="font-medium">{{ userName }}</span>
                      <span
                        v-else
                        class="cursor-pointer font-medium hover:underline"
                        @click="showProfileModal = true"
                      >
                        Set your name here
                      </span>
                      <button data-test="edit-profile-button" @click="showProfileModal = true">
                        <PencilAltIcon class="h-4 w-4 text-gray-700" />
                      </button>
                    </div>
                    <div
                      class="bg-bluedark-50 text-bluedark-700 inline-block rounded-full px-1 py-0.5 font-normal"
                    >
                      {{ user.email }}
                    </div>
                  </section>
                  <button
                    v-if="isAgentnoonUser"
                    class="w-full rounded-lg px-3.5 py-2 text-left whitespace-nowrap text-gray-700 hover:bg-gray-50"
                    @click="router.push({ name: 'Super Admin' })"
                  >
                    Super Admin
                  </button>
                  <button
                    data-test="mfa-button"
                    class="w-full rounded-lg px-3.5 py-2 text-left whitespace-nowrap text-gray-700 hover:bg-gray-50"
                    @click="redirectToMFA"
                  >
                    {{ hasMFA ? 'Update' : 'Setup' }} MFA
                  </button>
                  <button
                    data-test="update-authentication-button"
                    class="w-full rounded-lg px-3.5 py-2 text-left whitespace-nowrap text-gray-700 hover:bg-gray-50"
                    @click="redirectToLoginMethods"
                  >
                    Update Authentication
                  </button>
                  <a
                    href="mailto:support@agentnoon.com"
                    data-test="contact-support-button"
                    class="block w-full rounded-lg px-3.5 py-2 text-left whitespace-nowrap text-gray-700 hover:bg-gray-50"
                  >
                    Contact support
                  </a>
                  <button
                    data-test="test-network-button"
                    class="w-full rounded-lg px-3.5 py-2 text-left whitespace-nowrap text-gray-700 hover:bg-gray-50"
                    @click="showLatencyPanel = true"
                  >
                    Test Network
                  </button>
                  <button
                    data-test="sign-out-button"
                    class="bg-error-50 text-error-700 hover:bg-error-100 hover:text-error-700 active:bg-error-200 w-full rounded-lg px-3.5 py-2 whitespace-nowrap transition-colors"
                    @click="logout"
                  >
                    Sign out
                  </button>
                </section>
              </HeadlessMenuItems>
            </transition>
          </Menu>
          <ProfileModal
            :show="showProfileModal"
            @close="showProfileModal = false"
            @handle-submit="handleSubmitProfile"
          />
        </div>
      </section>
    </section>
    <LatencyTestPanel :open="showLatencyPanel" @close="showLatencyPanel = false" />
  </nav>
</template>

<script setup>
import HomeToolbar from '@/components/HomeToolbar.vue'
import Logo from '@/assets/Logo/Logomark.svg'
import AnalyticsIcon from '@/assets/untitled-ui/bar-chart-10.svg'
import ChartBreakout from '@/assets/untitled-ui/chart-breakout-square.svg'
import ListItemIcon from '@/assets/untitled-ui/list.svg'
import DataFlowIcon from '@/assets/untitled-ui/dataflow-04.svg'
import IconHome from '@/assets/untitled-ui/home-smile.svg'
import { CogIcon } from '@heroicons/vue/outline'
import IconHelp from '@/assets/untitled-ui/help-circle.svg'
import UserIcon from '@/assets/untitled-ui/users.svg'
import UsersGroupIcon from '@/assets/untitled-ui/layout-grid-01.svg'
import { ref, onMounted, computed } from 'vue'
import { onClickOutside } from '@vueuse/core'
import useBoard from '@/hooks/use-board'
import { useKeyboard } from '@/hooks/use-keyboard.js'
import useNavigation from '@/hooks/use-navigation'
import { FEATURES } from '@/lib/Features'
import { useMagicKeys } from '@vueuse/core'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { PencilAltIcon } from '@heroicons/vue/outline'
import { Menu, MenuButton, MenuItems as HeadlessMenuItems } from '@headlessui/vue'
import ProfileModal from '@/components/ProfileModal.vue'
import PersonIcon from '@/assets/SvgIcons/PersonIcon.svg'
import LatencyTestPanel from '@/components/LatencyTestPanel.vue'
import NotificationsMenu from '@/components/notifications/NotificationsMenu.vue'
import { isSuperAdminUser } from '@/utils/admin-users'

const { navigationConfig } = useNavigation()
const router = useRouter()
const route = useRoute()
const { boardId } = useBoard()
const { getters, dispatch } = useStore()
const { isUsingInput } = useKeyboard()
const isLoggedIn = computed(() => getters.isLoggedIn)
const isAppLoading = computed(() => getters.isAppLoading)
const isFetchingBoards = computed(() => getters.isFetchingBoards)
const hasInvitations = computed(() => getters.hasPendingInvitations)
const { boards } = useBoard()
const hasPlanInvites = computed(() => getters.hasUserPlanInvites)
const isAdmin = computed(() => getters.isAdmin(boardId.value))

const landingUrl = computed(() => `/${boardId.value}`)
const logo = computed(() => getters['branding/logo'])

const showProfileModal = ref(false)
const showLatencyPanel = ref(false)

const views = computed(() => [
  {
    name: FEATURES.OrgChart,
    icon: DataFlowIcon,
    tooltipText: 'Chart view',
    dataTest: 'main-org-view-button',
    keyboardShortcut: '1'
  },
  {
    name: FEATURES.Directory,
    icon: ListItemIcon,
    tooltipText: 'List view',
    dataTest: 'main-list-view-button',
    keyboardShortcut: '2'
  },
  {
    name: FEATURES.Forecast,
    icon: ChartBreakout,
    dataTest: 'main-forecast-view-button',
    tooltipText: 'Forecast view',
    keyboardShortcut: '3'
  },
  {
    name: FEATURES.Hub,
    icon: AnalyticsIcon,
    dataTest: 'main-hub-view-button',
    tooltipText: 'Workforce hub view',
    keyboardShortcut: '4'
  }
])

const user = computed(() => getters.user)
const isAgentnoonUser = computed(() => isSuperAdminUser(user.value?.email))
const userDisplayName = computed(() => getters.userDisplayName)
const hasMFA = computed(() => getters.isMFAEnabled ?? false)
const userPic = computed(() => getters.userPic)

const userName = computed(() => {
  if (userDisplayName.value) {
    return userDisplayName.value.split(' ')[0] || userDisplayName.value
  }
  return null
})

const redirectToMFA = () => {
  router.push({ path: '/setup-user' })
}

const redirectToLoginMethods = () => {
  router.push({ path: '/update-authentication' })
}

const logout = async () => {
  await dispatch('logout')

  router.push({ path: '/' })
}

const shouldShow = computed(() => {
  const result =
    !isAppLoading.value &&
    !isFetchingBoards.value &&
    boards.value.length === 0 &&
    !hasPlanInvites.value &&
    !hasInvitations.value

  return result
})

useMagicKeys({
  passive: false,
  onEventFired(e) {
    if (isUsingInput.value || e.type !== 'keydown') return

    const keyIndex = parseInt(e.key) - 1
    if (keyIndex >= 0 && keyIndex < views.value.length) {
      const menuItem = views.value[keyIndex]
      router.push({ path: navigationConfig.value[menuItem.name]?.url })
    }
  }
})

const handleSubmitProfile = async (displayName) => {
  await dispatch('updateName', { displayName })
}

const isSettingsOpen = ref(false)
const settingsRef = ref(null)

const closeSettings = () => {
  isSettingsOpen.value = false
}

// Add click outside handler
onMounted(() => {
  onClickOutside(settingsRef, closeSettings)
})
</script>
